import React from 'react';
import PropTypes from 'prop-types';
import { AuthProvider } from './AuthContext';
import { ThemeProvider } from './ThemeContext';
import { ClientProvider } from './ClientContext';
import PreferencesSyncProvider from './PreferencesSyncProvider';

/**
 * Combined provider that wraps all context providers.
 * 
 * Nesting order (outermost to innermost): ThemeProvider -> ClientProvider -> AuthProvider -> PreferencesSyncProvider
 * 
 * - ThemeProvider is outermost because it has no dependencies on other contexts
 * - ClientProvider is in the middle and can access ThemeContext if needed
 * - AuthProvider is next and can access both ClientContext and ThemeContext if needed
 * - PreferencesSyncProvider is innermost so it can access both AuthContext and ThemeContext
 *   to sync server-persisted preferences on login and save changes back to the API
 * 
 * The children components can access all four contexts.
 */
export const CombinedProvider = ({ children }) => {
	return (
		<ThemeProvider>
			<ClientProvider>
				<AuthProvider>
					<PreferencesSyncProvider>
						{children}
					</PreferencesSyncProvider>
				</AuthProvider>
			</ClientProvider>
		</ThemeProvider>
	);
};

CombinedProvider.propTypes = {
	children: PropTypes.node.isRequired
};

// Re-export contexts for convenience
export { AuthContext, AuthProvider } from './AuthContext';
export { ThemeContext, ThemeProvider } from './ThemeContext';
export { ClientContext, ClientProvider } from './ClientContext';
export { default as PreferencesSyncProvider } from './PreferencesSyncProvider';

// Keep AppContext export for backward compatibility during migration
export { AppContext, AppProvider } from './context';
